/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import individualDiagnose from "../../../../share/util/individualDiagnose";
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED, } from "../../../../../../share/src/util/api/polling";
import session from "../../../../share/session";
import individualWorkSupportUtil from "../../../../share/util/individualWorkSupportUtil";
import { apiErrorDefaultAction } from "../../../../../src/share/util/api/errorAction";

/** 各フェーズ定義 */
const PHASE = {
  STANDBY: "standby",
  START_CONFIRMATION: "startConfirmation", // 開始確認
  CAUTION: "caution", // 警告表示
  EXECUTION: "execution", // 実行監視
  SUCCESS: "success", // 停止
  FAILED: "failed",  // 共通エラー
};

export default {
  name: "requirement106",
  data() {
    return {
      phase: PHASE.STANDBY,
      isLoading: false,
      systemId: '',
      // ダイアログフラグ
      modal_requirement_106: false,
      buttonLabel: "individual.label_button_finish",
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true },
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.CAUTION:
          return "warning";
        case PHASE.FAILED:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        default:
          return "secondary";
      }
    },

    headerTitle() {
      // 開始確認
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 実行監視
      const ExecutionTitle = 'individual.common_title_execution';
      // 停止
      const SuccessTitle = 'message.common_title_success';
      // 警告表示
      const WarningTitle = 'message.common_title_warning';
      // 共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        [PHASE.STANDBY]: ExecutionTitle,
        [PHASE.START_CONFIRMATION]: ConfirmationTitle,
        [PHASE.EXECUTION]: ExecutionTitle,
        [PHASE.SUCCESS]: SuccessTitle,
        [PHASE.CAUTION]: WarningTitle,
        [PHASE.FAILED]: ErrorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        [PHASE.START_CONFIRMATION]: `${this.i18nWsKey}.REQ_WS_106_TXT_1`,
        [PHASE.CAUTION]: `${this.i18nWsKey}.REQ_WS_106_TXT_2`,
        [PHASE.EXECUTION]: `${this.i18nWsKey}.REQ_WS_106_TXT_3_0001`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_106_TXT_4_0002`,
        [PHASE.FAILED]: 'individual.error_communication_error'
      };
      return this.$t(messageMap[this.phase]);
    },

    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.START_CONFIRMATION]: true,
        [PHASE.CAUTION]: true,
      };
      return showCancelButtonMap[this.phase] ?? false;
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_106 = true;
    },

    onOkClicked() {
      switch (this.phase) {
        case PHASE.START_CONFIRMATION:
          this.phase = PHASE.CAUTION;
          break;
        case PHASE.CAUTION:
          this.execute();
          break;
        case PHASE.FAILED:
          this.exit(1);
          break;
        default:
          break;
      }
    },

    async onCancelClicked() {
      await this.exit(0);
    },

    async onEndClicked() {
      await this.stop();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** セッション遷移処理 */
    async standby() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 開始確認画面へ遷移
            this.phase = PHASE.START_CONFIRMATION;
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：汎用エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * ルーチン実行処理
     */
    async execute() {
      try {
        this.isLoading = true;
        this.phase = PHASE.EXECUTION;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: "execute",
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              this.phase = PHASE.SUCCESS;
              if (this.workSupportId === 10603) {
                this.buttonLabel = this.$t(`message.common_button_ok`);
              }
              break;
            //それ以外(エラー)
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 停止処理 */
    async stop() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'stop'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              await this.exit(0);
              break;
            case REQUEST_STATUS_FAILED:
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exit(intError) {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        if (intError === 0) {
          // エラー遷移あり
          if (response) {
            // 正常コールバック
            switch (response.data.status) {
              case REQUEST_STATUS_COMPLETED:
                // 正常終了：機能終了
                this.closeWizard();
                break;
              case REQUEST_STATUS_FAILED:
              default:
                // 異常終了：汎用エラー
                this.showUnexpectedError();
                return;
            }
          }
        } else {
          // エラー遷移なし：機能終了
          this.closeWizard();
          return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_106 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
    // セッション遷移処理
    await this.standby();
  },
};
